import { LtlQuoteLoadItemViewModel } from '@store/orders/types';

/**
 * True when the item is a breakdown sub-item of a mixed-commodity load item
 * rather than a load item in its own right.
 *
 * Sub-items only carry weight and class; units, unit type and dimensions belong
 * to the parent row, so the API leaves them unset on the child.
 */
export const isBreakdownChild = (item: LtlQuoteLoadItemViewModel): boolean =>
  item.parentQuoteLoadItemId != null || item._isBreakdown === true;

/**
 * Normalises load items into parent rows only, with every sub-item nested
 * under its parent in `quoteLoadItemBreakdown`.
 *
 * The API returns sub-items in two shapes, and both occur: nested under the
 * parent as `quoteLoadItemBreakdown`, or as their own top-level rows linked
 * back by `parentQuoteLoadItemId`. Anything comparing or counting load items
 * has to handle both, otherwise top-level children are mistaken for parents.
 * Nested children keep their order and come before flat ones.
 */
export const nestLoadItems = (
  loadItems?: LtlQuoteLoadItemViewModel[],
): LtlQuoteLoadItemViewModel[] => {
  const items = loadItems ?? [];
  const childrenByParent = new Map<string, LtlQuoteLoadItemViewModel[]>();
  const parents: LtlQuoteLoadItemViewModel[] = [];

  for (const item of items) {
    if (item.parentQuoteLoadItemId != null) {
      const key = String(item.parentQuoteLoadItemId);
      childrenByParent.set(key, [...(childrenByParent.get(key) ?? []), item]);
    } else {
      parents.push(item);
    }
  }

  return parents.map((parent) => {
    const flat =
      parent.id != null ? (childrenByParent.get(String(parent.id)) ?? []) : [];
    const nested = parent.quoteLoadItemBreakdown ?? [];
    if (flat.length === 0) return parent;
    return { ...parent, quoteLoadItemBreakdown: [...nested, ...flat] };
  });
};

/**
 * Normalises load items into display order -- each parent followed by its
 * breakdown sub-items -- and stamps `_isBreakdown` on the children so callers
 * can test a single flag via `isBreakdownChild`. Handles both API shapes via
 * `nestLoadItems`.
 */
export const flattenLoadItems = (
  loadItems?: LtlQuoteLoadItemViewModel[],
): LtlQuoteLoadItemViewModel[] =>
  nestLoadItems(loadItems).flatMap((parent) => [
    parent,
    ...(parent.quoteLoadItemBreakdown ?? []).map((child) => ({
      ...child,
      _isBreakdown: true,
    })),
  ]);
